<template>
    <div class="exam-result-management">
        <h3> 시스템 설정 > 검사 로그/이벤트 기록</h3>
        <!-- 검색/필터 영역 -->
        <div class="search-controls">
            <div class="search-row">
                <select v-model="filter.searchType" class="search-type">
                    <option value="equipmentName">장비명</option>
                    <option value="serialNumber">시리얼 번호</option>
                    <option value="modelName">모델명</option>
                </select>
                <input v-model="filter.searchText" placeholder="검색어 입력" class="search-input" maxlength="100" />
            </div>
            <div class="search-row">
                <input type="date" v-model="filter.startDate" class="date-input" />
                <span>~</span>
                <input type="date" v-model="filter.endDate" class="date-input" />
            </div>
            <div class="search-button-row">
                <button @click="search()" class="search-btn">검색</button>
            </div>
        </div>

        <!-- 목록 정렬 드롭다운 -->
        <div class="list-sort">
            <select v-model="sortOption">
                <option value="list">목록 정렬</option>
                <option value="recently">최근 발생 시간</option>
                <option value="deviceName">장비명</option>
                <option value="serialNumber">시리얼 번호</option>
                <option value="modelName">모델명</option>
            </select>
        </div>

        <!-- 결과 목록 테이블 -->
        <table class="result-table">
            <thead>
            <tr>
                <th>no</th>
                <th>장비명</th>
                <th>시리얼 번호</th>
                <th>모델명</th>
                <th>발생 시간</th>
                <th>이벤트 유형</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, idx) in filteredResults" :key="idx">
                <td>{{ (currentPage - 1) * pageSize + idx + 1 }}</td>
                <td>{{ row.device_name }}</td>
                <td>{{ row.serial_number }}</td>
                <td>{{ row.model_name }}</td>
                <td>{{ formatDate(row.created_at) }}</td>
                <td>{{ row.log_message }}</td>
            </tr>
            </tbody>
        </table>

        <pagination
            :total-items="totalCount"
            :page-size="pageSize"
            v-model="currentPage"
            @page-change="onPageChange"
        />
    </div>
</template>

<script>
import api, {urls} from "@/api";
import Pagination from "@/components/common/Pagination.vue";

export default {
    name: "LogEvent",
    components: {Pagination},
    data() {
        return {
            filter: {
                searchType: "equipmentName", // patientName or patientNo
                searchText: "",
                startDate: "",
                endDate: "",
            },
            sortOption: "list",
            eventLogList: [],


            currentPage: 1,       // 현재 페이지 번호
            pageSize: 10,         // 한 페이지에 보여줄 개수 (필요에 따라 변경 가능)
            totalPages: 1,        // 총 페이지 수 (백엔드에서 받아옴)
            totalCount: 0,        // 총 데이터 개수 (옵션, 백엔드에서 받아옴)
            isLoading: false,
        };
    },
    computed: {
        // 현재 페이지에 보여줄 데이터만 자바스크립트에서 슬라이싱
        filteredResults() {
            const list = this.eventLogList || [];
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            return list.slice(start, end);
        },
    },
    methods: {
        onPageChange(page) {
            this.getlist(page);
        },
        getlist(page = 1) {
            api.get(urls.system.deviceLogData)
                .then(res => {
                    const data = res.data;
                    this.eventLogList = data.list || [];
                    this.totalCount = data.totalCount || this.eventLogList.length;
                    this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                    this.currentPage = page;
                })
                .catch(err => {
                    alert("장비 목록 조회 실패");
                    this.eventLogList = [];
                    this.totalCount = 0;
                    this.totalPages = 1;
                    console.error(err);
                })
                .finally(() => {
                        this.isLoading = false;
                    });
        },
        search(page = 1) {
            const params = {
                searchType: this.filter.searchType,
                searchText: this.filter.searchText,
                startDate: this.filter.startDate,
                endDate: this.filter.endDate,
                sortOption: this.sortOption,
                page: page,
                pageSize: this.pageSize,
            };

            this.isLoading = true;
            api.post(urls.system.deviceLogSearch, params)
                .then(res => {
                    this.eventLogList = res.data.list || [];
                    this.totalCount = res.data.totalCount || this.eventLogList.length;
                    this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                    this.currentPage = page;

                    this.sortData();  // 데이터를 받은 후 정렬 실행
                })
                .catch(err => {
                    alert('로그 및 활동기록 목록 조회 실패');
                    this.eventLogList = [];
                    this.totalCount = 0;
                    this.totalPages = 1;
                    console.error(err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        formatDate(dateStr) {
            if (!dateStr) return "";
            const dt = new Date(dateStr);
            return dt.toLocaleString(); // 필요에 맞게 포맷팅 조정
        },
        sortData() {
            if (!this.eventLogList || this.eventLogList.length === 0) return;

            const option = this.sortOption;

            this.eventLogList.sort((a, b) => {
                switch(option) {
                    case 'recently':  // '최근'을 날짜 기준 오름차순 또는 내림차순으로 정렬 (아래는 내림차순)
                        return new Date(b.created_at) - new Date(a.created_at);

                    case 'deviceName': // 장비명 기준 정렬 (소문자로 비교해 정확도 향상 가능)
                        return (a.device_name || '').toLowerCase().localeCompare((b.device_name || '').toLowerCase());

                    case 'serialNumber': // 시리얼 번호 기준 정렬
                        return (a.serial_number || '').toLowerCase().localeCompare((b.serial_number || '').toLowerCase());

                    case 'modelName': // 모델명 기준 정렬
                        return (a.model_name || '').toLowerCase().localeCompare((b.model_name || '').toLowerCase());

                    default:           // 'list' 등 기본값일 때 정렬 안함
                        return 0;
                }
            });
        },


    },
    mounted() {
        this.getlist();
    },
    watch: {
        sortOption() {
            this.sortData();
        }
    },
};
</script>